import { WA_TEMPLATE_BODIES, type MockThread } from './whatsappMockData';

export type MockApiRef = { method: string; path: string; note?: string };

export type MockSuggestion = {
  id: string;
  title: string;
  why: string;
  eventKey: string | null;
  body: string;
  apis: MockApiRef[];
};

function fill(template: string, vars: Record<string, string>) {
  return String(template || '').replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    vars[key] != null && vars[key] !== '' ? String(vars[key]) : `{{${key}}}`
  );
}

function varsFromThread(thread: MockThread) {
  return {
    kolName: thread.kolName,
    campaignName: thread.campaignName,
    agencyName: thread.agencyName,
    brandName: thread.brandName,
    invitationUrl: thread.invitationUrl,
    dashboardUrl: thread.dashboardUrl,
    comment: thread.comment || '(no comment)',
    action:
      thread.invitationStatus === 'MODIFICATION_REQUESTED' ? 'requested_modification' : 'accepted',
    count: '1',
    actorName: 'Agency Admin',
  };
}

export function stageLabel(thread: MockThread): string {
  if (thread.paymentStatus === 'PENDING' || thread.paymentStatus === 'OVERDUE') return 'Payment';
  if (thread.submissionStatus) return 'Submission';
  if (thread.scriptStatus) return 'Script';
  return 'Invitation';
}

export function suggestionsForThread(thread: MockThread): MockSuggestion[] {
  const v = varsFromThread(thread);
  const T = WA_TEMPLATE_BODIES;
  const out: MockSuggestion[] = [];

  if (thread.invitationStatus === 'PENDING') {
    out.push({
      id: 'invite-reminder',
      title: 'Invitation reminder',
      why: 'Invitation is still PENDING — KOL has not accepted/rejected.',
      eventKey: 'INVITATION_SENT',
      body: fill(T.FOLLOW_UP_PENDING, v),
      apis: [
        { method: 'POST', path: '/api/jobs/:jobId/broadcast', note: 'Original invite enqueue' },
        { method: 'POST', path: '/api/invitations/:id/resend-invite-email', note: 'Email resend (not WA)' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
        { method: 'POST', path: '/api/ai-messages/generate-follow-up', note: 'AI copy only' },
      ],
    });
  }

  if (thread.invitationStatus === 'MODIFICATION_REQUESTED') {
    out.push({
      id: 'mod-followup',
      title: 'Modification follow-up',
      why: 'KOL requested changes — respond in-app, then optionally ping on WhatsApp.',
      eventKey: 'INVITATION_RESPONDED',
      body: fill(T.MODIFICATION_REPLY, v),
      apis: [
        {
          method: 'POST',
          path: '/api/invitations/:id/respond-to-modification',
          note: 'Agency decision → INVITATION_RESPONDED',
        },
        { method: 'POST', path: '/api/ai-messages/generate-modification-response', note: 'AI copy only' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.scriptStatus === 'REVISION_REQUESTED') {
    out.push({
      id: 'script-rev-reminder',
      title: 'Script revision reminder',
      why: 'Script status REVISION_REQUESTED — nudge KOL to update draft.',
      eventKey: 'CONTENT_REVISED',
      body: fill(T.FOLLOW_UP_SCRIPT_REVISION, v),
      apis: [
        {
          method: 'POST',
          path: '/api/scripts/:id/request-revision',
          note: 'Triggers CONTENT_REVISED automation',
        },
        { method: 'POST', path: '/api/ai-messages/generate-script-revision', note: 'AI copy only' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.scriptStatus === 'SUBMITTED_FOR_BRAND') {
    out.push({
      id: 'script-submitted-ack',
      title: 'Script received (agency note)',
      why: 'Script awaiting brand review — optional acknowledgement to KOL.',
      eventKey: 'SCRIPT_SUBMITTED',
      body: fill(
        'Hi {{kolName}}, we received your script for {{campaignName}} and it is under review. Thanks!',
        v
      ),
      apis: [
        { method: 'POST', path: '/api/scripts/:id/submit', note: 'KOL/agency submit → SCRIPT_SUBMITTED' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.scriptStatus === 'APPROVED_BY_BRAND' && !thread.submissionStatus) {
    out.push({
      id: 'shoot-reminder',
      title: 'Film & submit reminder',
      why: 'Script approved — waiting for first content submission.',
      eventKey: 'SCRIPT_APPROVED',
      body: `${fill(T.SCRIPT_APPROVED, v)} Please upload your video when ready.`,
      apis: [
        { method: 'POST', path: '/api/scripts/:id/approve', note: 'Triggers SCRIPT_APPROVED' },
        { method: 'POST', path: '/api/submissions', note: 'KOL creates submission next' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.submissionStatus === 'REVISION_REQUESTED') {
    out.push({
      id: 'video-rev-reminder',
      title: 'Video revision reminder',
      why: 'Submission REVISION_REQUESTED — ask KOL to re-upload.',
      eventKey: 'CONTENT_REVISED',
      body: fill(T.FOLLOW_UP_SUBMISSION_REVISION, v),
      apis: [
        {
          method: 'POST',
          path: '/api/submissions/:id/request-revision',
          note: 'Triggers CONTENT_REVISED',
        },
        { method: 'POST', path: '/api/ai-messages/generate-submission-revision', note: 'AI copy only' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.submissionStatus === 'SUBMITTED' || thread.submissionStatus === 'PENDING_AGENCY_QA') {
    out.push({
      id: 'submission-ack',
      title: 'Submission received',
      why: 'Content is in review queue — optional KOL acknowledgement.',
      eventKey: 'CONTENT_SUBMITTED',
      body: fill(
        'Hi {{kolName}}, we got your submission for {{campaignName}} and will review shortly. — {{agencyName}}',
        v
      ),
      apis: [
        { method: 'POST', path: '/api/submissions', note: 'Creates CONTENT_SUBMITTED' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.paymentStatus === 'PENDING' || thread.paymentStatus === 'OVERDUE') {
    out.push({
      id: 'payment-reminder',
      title: 'Payment reminder (manual)',
      why: `Payment is ${thread.paymentStatus} — production has no MessagingEventKey for payments yet. Manual WhatsApp only.`,
      eventKey: null,
      body: fill(T.PAYMENT_REMINDER_MANUAL, v),
      apis: [
        {
          method: 'POST',
          path: '/api/jobs/:jobId/payment-milestones/:pmId/remind',
          note: 'In-app only today',
        },
        {
          method: 'POST',
          path: '/api/jobs/:jobId/invoices/:invoiceId/send-reminder',
          note: 'Email reminder',
        },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  if (thread.submissionStatus === 'APPROVED' && !thread.paymentStatus) {
    out.push({
      id: 'content-approved-followup',
      title: 'Content approved (confirm)',
      why: 'Submission APPROVED — confirm next steps / wrap-up.',
      eventKey: 'CONTENT_APPROVED',
      body: fill(T.CONTENT_APPROVED, v),
      apis: [
        { method: 'POST', path: '/api/submissions/:id/approve', note: 'Triggers CONTENT_APPROVED' },
        { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      ],
    });
  }

  out.push({
    id: 'custom-nudge',
    title: 'Custom check-in',
    why: 'Generic manual nudge — no automation event; use inbox send or AI improve-message.',
    eventKey: null,
    body: fill(
      'Hi {{kolName}}, quick check-in on {{campaignName}} from {{agencyName}}. Any blockers? {{dashboardUrl}}',
      v
    ),
    apis: [
      { method: 'POST', path: '/api/ai-messages/improve-message', note: 'AI rewrite only' },
      { method: 'POST', path: '/api/whatsapp-inbox/threads/:threadId/messages', note: 'Manual WA send' },
      { method: 'GET', path: '/api/notification-outbox', note: 'Monitor deliveries' },
    ],
  });

  return out;
}
